'use client'

import { useWorldDetailsContext } from '@/context/WorldDetailsContext'
import { fetchJwtToken } from '@/server/services/token'
import { Room } from '@/services/cms'
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

interface WorldIframeTokenContextType {
  jwtToken: string | null
}

const WorldIframeTokenContext = createContext<WorldIframeTokenContextType | undefined>(undefined)

type WorldIframeTokenProviderProps = PropsWithChildren<{
  initialRoomData: Room
}>

export function WorldIframeTokenProvider({
  initialRoomData,
  children,
}: WorldIframeTokenProviderProps) {
  const { worldMeta } = useWorldDetailsContext()
  const [jwtToken, setJwtToken] = useState<string | null>(null)

  const roomId = initialRoomData.hub_sid
  const shouldGenerateJwtToken = Boolean(
    worldMeta && worldMeta.scene_type !== 'standalone' && roomId,
  )

  useEffect(() => {
    if (!shouldGenerateJwtToken) {
      return
    }

    const createJwtToken = async () => {
      // 🥷🏼 use this way to do server action in useEffect
      const token = await fetchJwtToken(roomId)
      setJwtToken(token)
    }

    createJwtToken()
  }, [roomId, shouldGenerateJwtToken])

  return (
    <WorldIframeTokenContext.Provider value={{ jwtToken }}>
      {children}
    </WorldIframeTokenContext.Provider>
  )
}

export function useWorldIframeToken() {
  const context = useContext(WorldIframeTokenContext)
  if (context === undefined) {
    throw new Error('useWorldIframeToken must be used within a WorldIframeTokenProvider')
  }
  return context
}
